import { Box, TableRow, TableHead, TableContainer, TableCell, TableBody, Typography, Dialog, Rating, Button, TextField, Stack } from "@mui/material";
import { AdvisorTitle } from "../components/AdvisorTitle";
import { useDealContext } from "../../deal/context";
import { useAdvisor } from "../context/AdvisorContext";
import useAnalytics from "../../../hooks/useAnalytics";
import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AdvisorTable, ExecTabs, ExecTab } from "../../company/styles/AdvisorStyles";
import { DateDisplay } from "../../meeting/components/DateDisplay";
import ClientProvider from "../../../modules/ClientProvider";
import { useAuth0 } from "@auth0/auth0-react";
import { CalendlyScheduler, MeetingLinks } from "../../meeting/components/CalendlyScheduler";
import { RatingDialog } from "../components/meetings/RatingDialog";
import { RATING_LABELS } from "../../../constants/ratings";
import { SchedulerType } from "../../../types";


export const AdvisorMeet = () => {
    const { dealContext } = useDealContext();
    const project = dealContext.project;
    const { advisor, loading, fetchAdvisor } = useAdvisor();
    const { trackAdvisorPage, trackAdvisorEvent } = useAnalytics();
    const [isDemoSchedulerOpen, setIsDemoSchedulerOpen] = useState(false);
    const [scheduler, setScheduler] = useState<any | null>(null);
    const [meetingDetails, setMeetingDetails] = useState<any>(null);
    const [meetings, setMeetings] = useState<any>([]);
    const [initialMeetingUuid, setInitialMeetingUuid] = useState<string | null>(null);
    const { meetingUuid } = useParams();
    const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
    const [rating, setRating] = useState<number | null>(null);
    const initialRating = new URLSearchParams(window.location.search).get("rt");
    const [selectedMeetingTypeIndex, setSelectedMeetingTypeIndex] = useState<number>(0);
    const [meetingTypes, setMeetingTypes] = useState<SchedulerType[]>([]);
    const [isLoadingSchedulers, setIsLoadingSchedulers] = useState(false);
    const [schedulerError, setSchedulerError] = useState<string | null>(null);

    // Map tab indices to scheduler types - only include INTRO/DEMO if intro_enabled is true
    const potentialMeetingTypes: SchedulerType[] = advisor?.intro_enabled ? [SchedulerType.INTRO, SchedulerType.DEMO, SchedulerType.TRIAL] : [SchedulerType.TRIAL];
    const selectedMeetingType = meetingTypes[selectedMeetingTypeIndex];
    const [schedulers, setSchedulers] = useState<{[key in SchedulerType]?: any}>({});

    const advisorHttpClient = ClientProvider.provideAdvisorClient();

    const { getAccessTokenSilently } = useAuth0();

    // Helper function to check if a scheduler is valid and available
    const isSchedulerAvailable = useCallback((scheduler: any) => {
        return scheduler && 
               scheduler.scheduling_url && 
               scheduler.scheduling_url.trim() !== "" && 
               scheduler.is_active !== false;
    }, []);

    useEffect(() => {
        if (meetingUuid) {
            setInitialMeetingUuid(meetingUuid);
            // Remove meetingUuid from URL path
            window.history.replaceState({}, '', window.location.pathname.replace(`/meet/${meetingUuid}`, `/meet`));
        }
        if (initialRating !== null) {
            setRating(parseInt(initialRating));
            // Remove rt from URL search params
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete('rt');
            window.history.replaceState({}, '', window.location.pathname + (searchParams.toString() ? '?' + searchParams.toString() : ''));
        }
    }, [meetingUuid, initialRating]);

    // Add new effect to handle setting selected meeting when meetings are loaded
    useEffect(() => {
        if (initialMeetingUuid && meetings.length > 0) {
            const matchingMeeting = meetings.find((meeting: any) => meeting.uuid === initialMeetingUuid);
            if (matchingMeeting) {
                setSelectedMeeting(matchingMeeting);
            }
        }
    }, [initialMeetingUuid, meetings]);

    useEffect(() => {
        if (advisor) {
            trackAdvisorPage(advisor, 'expert flow - engage');
        }
    }, [advisor]);

    useEffect(() => {
        if (window.location.hash === "#calendar" && advisor && !advisor.prospect_scheduled) {
            setIsDemoSchedulerOpen(true);
            trackAdvisorEvent(advisor, 'Viewed calendar');
        }
    }, []);

    useEffect(() => {
        // Fetch the scheduler data for all types
        if (advisor?.public_id) {
            // First fetch meetings, then fetch schedulers
            fetchMeetingsAndSchedulers();
        }

        return () => {
            setScheduler(null);
            setSchedulers({});
        }
    }, [advisor?.public_id]);

    useEffect(() => {
        fetchMeetingDetails();
    }, [advisor?.prospect_event_invitee_uri]);

    // Combined function to fetch meetings first, then schedulers
    const fetchMeetingsAndSchedulers = async () => {
        if (!advisor?.public_id) {
            return;
        }

        try {
            // First fetch meetings and get the data directly
            const meetingsData = await fetchMeetings();
            // Then fetch schedulers using the meetings data
            await fetchAllSchedulers(meetingsData);
        } catch (error) {
            console.error('Error in fetchMeetingsAndSchedulers:', error);
        }
    };

    const fetchMeetingDetails = async () => {
        if (advisor?.prospect_event_invitee_uri) {
            const accessToken = await getAccessTokenSilently();
            const inviteeId = advisor.prospect_event_invitee_uri.split("/invitees/")[1];
            const { data, error } = await advisorHttpClient.getCalendlyMeetingDetails(project.tag, inviteeId, accessToken);
            if (data) {
                if (data.invitee.canceled && !data.invitee.rescheduled) {
                    setMeetingDetails(null);
                    fetchAdvisor(project.tag);
                } else {
                    setMeetingDetails(data.invitee);
                }
            } else {
                console.error('Error fetching meeting details:', error);
            }
        }
    };

    const handleMeetingEvent = async (e: any, meetingUuid: string) => {
        if (!advisor?.public_id) {
            return;
        }
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await advisorHttpClient.getAdvisorMeeting(project.tag, meetingUuid, accessToken);
        if (data) {
            const meeting = data.meeting;
            setMeetings(meetings.map((m: any) => m.id === meeting.id ? meeting : m));
        }
    }

    const fetchMeetings = async () => {
        if (!advisor?.public_id) {
            return [];
        }
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await advisorHttpClient.getAdvisorMeetings(project.tag, accessToken);
        if (data) {
            const filteredMeetings = data.meetings.filter((meeting: any) => meeting.scheduler?.type !== "onboarding");
            setMeetings(filteredMeetings);
            setIsDemoSchedulerOpen(data.meetings.filter((meeting: any) => meeting.status === "scheduled").length === 0);
            return filteredMeetings;
        }
        return [];
    }

    const handleCloseDemoScheduler = () => {
        fetchMeetingsAndSchedulers();
    }

    const handleRating = async (meetingUuid: string, rating: number | null, comment: string | null) => {
        if (rating === null) {
            return;
        }
        if (!advisor?.public_id) {
            return;
        }
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await advisorHttpClient.advisorRateMeeting(project.tag, meetingUuid, rating, comment, accessToken);
        if (data) {
            fetchMeetings();
        }
    }

    const handleCloseRatingDialog = () => {
        setSelectedMeeting(null);
        setRating(null);
        setInitialMeetingUuid(null);
    }

    const fetchAllSchedulers = async (meetingsData?: any[]) => {
        if (!advisor?.public_id) {
            return;
        }
        
        // Use passed meetings data or fall back to state
        const currentMeetings = meetingsData || meetings;
        
        setIsLoadingSchedulers(true);
        setSchedulerError(null);
        
        try {
            const accessToken = await getAccessTokenSilently();
            const schedulerPromises = potentialMeetingTypes.map(async (type) => {
                try {
                    const { data, error } = await advisorHttpClient.getScheduler(project.tag, type, accessToken);
                    return { type, scheduler: error ? null : data?.scheduler, error };
                } catch (err) {
                    console.error(`Error fetching scheduler for type ${type}:`, err);
                    return { type, scheduler: null, error: err };
                }
            });

            const results = await Promise.all(schedulerPromises);
            const schedulerMap: {[key in SchedulerType]?: any} = {};
            const errors: string[] = [];
            
            results.forEach(({ type, scheduler, error }) => {
                if (scheduler && isSchedulerAvailable(scheduler)) {
                    schedulerMap[type] = scheduler;
                } else if (error) {
                    errors.push(`Failed to load ${type} scheduler`);
                } else {
                    //console.log(`Scheduler not available for type ${type}:`, scheduler);
                }
            });

            setSchedulers(schedulerMap);
            
            // Build available meeting types list using current meetings data
            const tempMeetingTypes = [];
            
            if (advisor.intro_enabled) {
                // When intro is enabled, show INTRO and DEMO if not already scheduled
                if (schedulerMap[SchedulerType.INTRO] && !currentMeetings.some((meeting: any) => meeting.scheduler?.type === SchedulerType.INTRO)) {
                    tempMeetingTypes.push(SchedulerType.INTRO);
                }
                if (schedulerMap[SchedulerType.DEMO] && !currentMeetings.some((meeting: any) => meeting.scheduler?.type === SchedulerType.DEMO)) {
                    tempMeetingTypes.push(SchedulerType.DEMO);
                }
            }
            
            // Always show TRIAL if available (regardless of intro_enabled)
            if (schedulerMap[SchedulerType.TRIAL]) {
                tempMeetingTypes.push(SchedulerType.TRIAL);
            }

            setMeetingTypes(tempMeetingTypes);

            // Set initial scheduler selection
            if (tempMeetingTypes.length > 0) {
                const initialType = tempMeetingTypes[0];
                setSelectedMeetingTypeIndex(0);
                setScheduler(schedulerMap[initialType]);
            }

            if (errors.length > 0) {
                setSchedulerError(errors.join(', '));
            }
        } catch (error) {
            console.error('Error fetching schedulers:', error);
            setSchedulerError('Failed to load meeting schedulers');
        } finally {
            setIsLoadingSchedulers(false);
        }
    };

    const handleMeetingTypeChange = (event: any, newTabIndex: number) => {
        const newType = meetingTypes[newTabIndex];
        // Only allow selection if scheduler exists and is available for this type
        if (schedulers[newType] && isSchedulerAvailable(schedulers[newType])) {
            setSelectedMeetingTypeIndex(newTabIndex);
            setScheduler(schedulers[newType]);
        }
    };

    const handleOpenScheduler = () => {
        // Only open if current selection has a valid scheduler
        if (scheduler && isSchedulerAvailable(scheduler)) {
            setIsDemoSchedulerOpen(true);
            trackAdvisorEvent(advisor, `Viewed ${selectedMeetingType} calendar`);
        }
    };

    // Check if we have any schedulers available
    const hasAnyScheduler = Object.values(schedulers).some((scheduler: any) => isSchedulerAvailable(scheduler));

    const getMeetingTypeTitle = (type: SchedulerType) => {
        return project.effective_scheduler_properties?.[type]?.name ||
            (type === SchedulerType.INTRO ? "Company Intro Meeting" :
                type === SchedulerType.DEMO ? "Product Feedback Meeting" :
                    "Trial Exploration Meeting");
    };

    const getMeetingTypeDescription = (type: SchedulerType) => {
        return project.effective_scheduler_properties?.[type]?.description ||
            (type === SchedulerType.INTRO ? "A short, 15 minute meeting to welcome you and introduce you to the Founder or company sponsor." :
                type === SchedulerType.DEMO ? "30-minute video call with a PM to introduce you to the product and get feedback on new features or messaging." :
                    "30-minute call with a sales specialist who can provide details on a commercial deployment.");
    };

    // Effect to update scheduler when schedulers or selectedMeetingType changes
    useEffect(() => {
        
        if (selectedMeetingType && schedulers[selectedMeetingType] && isSchedulerAvailable(schedulers[selectedMeetingType])) {
            setScheduler(schedulers[selectedMeetingType]);
        } else if (Object.keys(schedulers).length > 0) {
            // Find first available scheduler if current one is not available
            const firstAvailableType = meetingTypes.find(type => isSchedulerAvailable(schedulers[type]));
            if (firstAvailableType) {
                const firstAvailableIndex = meetingTypes.indexOf(firstAvailableType);
                setSelectedMeetingTypeIndex(firstAvailableIndex);
                setScheduler(schedulers[firstAvailableType]);
            }
        }
    }, [schedulers, selectedMeetingType, meetingTypes, isSchedulerAvailable, meetings]);

    if (!advisor) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <AdvisorTitle project={project} section="Meet" subheading={`Schedule a meeting with the ${project.name} team to explore a trial or partnership.`} />
            <div className="flex-column left-column">
                <div className="evaluation-content" id="evaluation-content">

                    {meetings?.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ marginBottom: '16px', fontWeight: 'bold' }}>
                                Your Meetings
                            </Typography>
                            <TableContainer>
                                <AdvisorTable>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className="center">
                                                Date
                                            </TableCell>
                                            <TableCell>
                                                Time
                                            </TableCell>
                                            <TableCell>
                                                Description
                                            </TableCell>
                                            <TableCell>
                                                Status
                                            </TableCell>
                                            <TableCell className="center">
                                                Action
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {meetings
                                            ?.sort((a: any, b: any) => new Date(a.event_start).getTime() - new Date(b.event_start).getTime())
                                            ?.map((meeting: any) => (
                                                <TableRow key={meeting.uuid}>
                                                    <TableCell className="center">
                                                        <DateDisplay meeting={meeting} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(meeting.event_start).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                                                    </TableCell>
                                                    <TableCell>
                                                        {meeting.scheduler?.type && project.effective_scheduler_properties?.[meeting.scheduler.type as keyof typeof project.effective_scheduler_properties]?.name}
                                                    </TableCell>

                                                    <TableCell>
                                                        {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1).toLowerCase()}
                                                    </TableCell>
                                                    <TableCell className="center">
                                                        {(meeting.status === "completed" || new Date(meeting.event_start) < new Date()) && (
                                                            <Box sx={{
                                                                minHeight: '60px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                flexDirection: 'column',
                                                                gap: '5px'
                                                            }}
                                                                onClick={() => setSelectedMeeting(meeting)}>
                                                                <Rating
                                                                    value={meeting.ratings?.length > 0 ? meeting.ratings[0].rating : null}
                                                                    max={4}
                                                                    readOnly
                                                                />
                                                                <Typography component="legend" variant="body2">
                                                                    {meeting.ratings?.length > 0 ? (
                                                                        RATING_LABELS[meeting.ratings[0].rating]
                                                                    ) : "Please rate the meeting"}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {(meeting.status === "scheduled" && new Date(meeting.event_start) > new Date()) && <MeetingLinks advisor={advisor} meeting={meeting} handleMeetingEvent={handleMeetingEvent} fetchMeetingDetails={fetchMeetingDetails} />}
                                                        {meeting.status === "requested" && <button onClick={() => setIsDemoSchedulerOpen(true)}>Schedule a Meeting</button>}

                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </AdvisorTable>
                            </TableContainer>


                        </>
                    )}

                    {/* Schedule New Meeting Section */}
                    {isLoadingSchedulers && (
                        <Box sx={{ textAlign: 'center', padding: '40px' }}>
                            <Typography variant="body1">Loading meeting schedulers...</Typography>
                        </Box>
                    )}

                    {schedulerError && (
                        <Box sx={{ textAlign: 'center', padding: '20px', color: 'error.main' }}>
                            <Typography variant="body2" gutterBottom>
                                {schedulerError}
                            </Typography>
                            <Button variant="outlined" onClick={() => fetchMeetingsAndSchedulers()} size="small">
                                Retry
                            </Button>
                        </Box>
                    )}

                    {!isLoadingSchedulers && !schedulerError && hasAnyScheduler && (
                        <Box sx={{ marginTop: meetings?.length > 0 ? '40px' : '0', marginBottom: '40px' }}>

                            {!isDemoSchedulerOpen && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleOpenScheduler}
                                        disabled={!scheduler || !isSchedulerAvailable(scheduler)}
                                    >
                                        Schedule a Meeting
                                    </Button>
                                </Box>
                            )}

                            {isDemoSchedulerOpen && scheduler && isSchedulerAvailable(scheduler) && (
                                <>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '30px' }}>
                                        {meetingTypes.length > 1 ? "What type of meeting would you like to schedule?" : "Interested in scheduling a meeting to learn more about a potential rollout?"}
                                    </Typography>
                                    {/* Meeting Type Selector */}
                                    <Box sx={{ marginBottom: '20px' }}>
                                        {meetingTypes.length > 1 && (
                                        <ExecTabs variant="fullWidth" value={selectedMeetingTypeIndex} onChange={handleMeetingTypeChange}>
                                            {meetingTypes.map((type, index) => (
                                                <ExecTab 
                                                    key={index}
                                                    label={getMeetingTypeTitle(type)}
                                                    disabled={!(schedulers[type] && isSchedulerAvailable(schedulers[type]))}
                                                />
                                            ))}
                                        </ExecTabs>
                                        )}
                                        <Typography variant="body1" sx={{ marginTop: '0', textAlign: 'center' }}>
                                            {getMeetingTypeDescription(selectedMeetingType)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        marginBottom: '20px',
                                        '@media (min-width: 800px)': {
                                            marginTop: '-60px'
                                        }
                                    }}>
                                        <CalendlyScheduler project={project} scheduler={scheduler} advisor={advisor} surveyLink={advisor?.survey_link} onMeetingCreated={handleCloseDemoScheduler} />
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    {!isLoadingSchedulers && !schedulerError && !hasAnyScheduler && (
                        <Box sx={{ textAlign: 'center', padding: '40px', color: 'text.secondary' }}>
                            <Typography variant="h6" gutterBottom>
                                Meeting schedulers are currently unavailable.
                            </Typography>
                            <Typography variant="body2">
                                Please <span className="link" onClick={() => (window as any).Intercom('show')}>contact us</span> to help you schedule a meeting.
                            </Typography>
                        </Box>
                    )}
                </div>
            </div>

            {selectedMeeting && (
                <RatingDialog
                    meeting={selectedMeeting}
                    advisor={advisor}
                    onRate={handleRating}
                    onClose={handleCloseRatingDialog}
                    suggestedRating={rating}
                />
            )}
        </>
    );
}